/** Request form keys backed by SQL DECIMAL columns — normalize before PATCH/POST. */
export const REQUEST_NUMERIC_FIELD_NAMES = [
  'powerBuswayLengthFt',
  'powerHowManyAmps',
  'powerMaxKwPerRack',
  'powerTotalAvailablePowerKw',
  'powerMainNominalLvVoltage',
  'powerAnticipatedFinalMwSize',
  'powerTotalDataCenterItLoadKw',
  'powerAverageItLoadPerRackKw',
  'powerHowManyOutlets',
  'powerMainNominalAmps',
  'siteRoomLengthFtIn',
  'siteRoomWidthSqftSqm',
  'siteRaisedFloorHeight',
  'siteCeilingHeight',
  'siteWeightSupportOfFloor',
  'siteDroppedCeilingHeight',
  'sitePlenumHeight',
  'siteSlabToDroppedCeilingHeight',
  'rackDimensionsLength',
  'rackDimensionsHeight',
  'rackDimensionsWidth',
  'lcTotalLiquidCoolingThermalLoadPerSfn',
  'lcNumberOfLiquidCooledRacksToBeServed',
  'lcNumberOfAirCooledRacks',
  'lcEthyleneGlycolPct',
  'lcTss',
  'lcTsr',
  'lcFlowrateRequirement',
  'lcMaximumPressureRating',
  'lcRackPressureDrop',
  'lcExternalStaticPressureRequirement',
  'lcDatahallRaisedFloorDepth',
] as const;

export type RequestNumericFieldName =
  (typeof REQUEST_NUMERIC_FIELD_NAMES)[number];

function coerceRequestNumericValue(value: unknown): number | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }
    const numValue = Number(trimmed);
    return Number.isFinite(numValue) ? numValue : null;
  }
  return null;
}

/**
 * Coerce string/invalid numeric request fields to finite numbers or null so SQL
 * DECIMAL bindings never receive "", NaN, or partial input like ".".
 */
export function normalizeRequestNumericFields<
  T extends Record<string, unknown>,
>(data: T, fieldNames: readonly string[] = REQUEST_NUMERIC_FIELD_NAMES): T {
  const out: Record<string, unknown> = { ...data };
  for (const field of fieldNames) {
    if (!(field in out)) {
      continue;
    }
    const raw = out[field];
    if (raw === undefined || raw === null) {
      continue;
    }
    out[field] = coerceRequestNumericValue(raw);
  }
  return out as T;
}
